<template>
  <main-large-dialog
    v-bind="$props"
    :is-visible.sync="computedIsVisible"
    :title="titleItem"
    dialog-width="90%"
    min-body-height="400px"
    :is-valid="currentTab === 0 ? isChartReady : isDataReady"
    @agree="saveHandler"
    max-height="95vh"
    no-paddings-y
    no-paddings-x
    :agree-text="agreeButtonText"
    disagree-text="dialog.disagreeClose"
  >
    <template #body>
      <v-tabs v-model="currentTab" ref="tabs" class="fill-height d-flex flex-column" hide-slider color="font">
        <v-tab
          v-for="tab in tabs"
          :key="tab.value"
          :disabled="tab.disabled"
          active-class="activeTab"
          class="font--text"
          style="text-transform: none;"
        >
          {{ tab.text }}
        </v-tab>
        <v-tabs-items v-model="currentTab" class="fill-height">
          <v-tab-item style="height: inherit">
            <v-row no-gutters class="pt-10 px-15" style="gap: 2rem">
              <v-col cols="3">
                <data-input v-bind="parameterInput" v-model="parameter" @change="onParamChange" />
              </v-col>
              <v-col cols="3">
                <data-input v-bind="dateInput" v-model="dates" @saveDate="onDateChange" />
              </v-col>
            </v-row>
            <v-row no-gutters class="justify-center align-center">
              <v-col v-if="isFetching">
                <main-loader />
              </v-col>
              <template v-else-if="!!mappedValues">
                <v-col v-if="!maxFeaturesExceeded" cols="10">
                  <canvas id="chart" role="img" height="500px"></canvas>
                </v-col>
                <v-col v-else cols="12">
                  <v-alert type="warning" icon="mdi-alert-outline" dense text class="text-left mx-15 text-caption">
                    {{ $i18n.t('dialog.billingWaterUsageAnalysis.moreFeaturesNotAllowed', { limit: maxFeatures }) }}
                  </v-alert>
                </v-col>
              </template>
            </v-row>
          </v-tab-item>
          <v-tab-item style="height: inherit">
            <v-row no-gutters class="fill-height">
              <v-col cols="12" class="px-5 py-6">
                <v-data-table
                  :headers="tableHeaders"
                  height="auto"
                  ref="billingWaterUsageTable"
                  class="billingWaterUsageTable"
                  hide-default-footer
                  :items-per-page="-1"
                  fixed-header
                  :items="tableItems"
                >
                  <template #[`body.append`]>
                    <tr class="sticky-footer-table elevation-3">
                      <td v-for="(value, key) in tableSummary" class="top-border" :key="key">
                        {{ value }}
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </template>
  </main-large-dialog>
</template>

<script>
import Chart from 'chart.js';
import { call, get } from 'vuex-pathify';

import MainLargeDialog from '@/components/MainLargeDialog';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'BillingWaterUsageAnalysisDialog',
  components: {
    MainLargeDialog,
    DataInput,
    MainLoader,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    parameter: null,
    dates: null,
    isDataReady: false,
    isChartReady: false,
    mappedValues: null,
    tableItems: null,
    tableHeaders: null,
    isFetching: false,
    currentTab: 0,
    maxFeatures: 20,
  }),
  watch: {
    selectedItems(nV, oV) {
      if (nV !== oV) {
        if (this.isChartReady) this.chart.destroy();
        this.dates = this.parameter = this.mappedValues = this.isChartReady = this.isDataReady = null;
        this.tableHeaders = this.tableItems = [];
        this.currentTab = 0;
      }
    },
  },
  computed: {
    billingWaterUsageAnalysisData: get('admin/modulesMapping@billing.water_usage_analysis'),
    maxFeaturesExceeded() {
      return this.selectedItems.length > this.maxFeatures;
    },
    tabs() {
      return [
        {
          text: this.$i18n.t('dialog.body.chart'),
          value: 'chart',
        },
        {
          text: this.$i18n.t('dialog.body.table'),
          value: 'table',
          disabled: !this.isDataReady,
        },
      ];
    },
    parameterInput() {
      return {
        dataType: {
          name: 'select',
        },
        itemValue: 'value',
        itemText: 'name',
        label: this.$i18n.t('dialog.billingWaterUsageAnalysis.parameter'),
        items: [
          {
            value: 'day_average',
            name: this.$i18n.t('dialog.billingWaterUsageAnalysis.day_average'),
          },
          {
            value: 'sum',
            name: this.$i18n.t('dialog.billingWaterUsageAnalysis.sum'),
          },
        ],
      };
    },
    dateInput() {
      return {
        dataType: {
          name: 'daterange',
        },
        datePickerProps: { type: 'month' },
        prependInnerIcon: 'mdi-calendar',
        label: this.$i18n.t('dialog.billingWaterUsageAnalysis.dates'),
      };
    },
    titleItem() {
      return this.$i18n.t(`dialog.billingWaterUsageAnalysis.${this.parameter ? this.parameter : 'analysis'}`);
    },
    agreeButtonText() {
      return this.currentTab === 0 ? 'dialog.downloadChart' : 'dialog.downloadTable';
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    tableSummary() {
      return Object.fromEntries(
        this.tableHeaders.map(header => {
          if (header.value === 'id')
            return [header.value, `${this.$i18n.t('dialog.billingWaterUsageAnalysis.tableSummary')}:`];
          else if (header.value === 'alias') return [header.value, ''];
          else {
            const t = this.tableItems.reduce((a, b) => {
              if (!b[header.value]) b[header.value] = 0;
              return a + b[header.value];
            }, 0);
            return [header.value, Number.parseFloat(t.toFixed(2))];
          }
        })
      );
    },
  },
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    onDateChange() {
      if (!this.parameter) return;
      this.getData();
    },
    onParamChange() {
      if (!this.isDataReady) {
        if (this.dates) this.getData();
        return;
      }
      this.chart.data.datasets = this.getChartDatasets();
      this.chart.update();
      this.setTableData();
    },
    setTableData() {
      let tableHeadersObject = {};
      this.tableItems = Object.entries(this.mappedValues).map(([key, value]) => {
        let summary = 0;
        value[`data_${this.parameter}`].forEach(item => {
          tableHeadersObject[item.x] = this.$dayjs.utc(item.x).format('YYYY-MM');
          summary += item.y;
        });
        return {
          id: key,
          alias: value.label,
          ...Object.fromEntries(value[`data_${this.parameter}`].map(dt => [dt.x, dt.y])),
          summary: Number.parseFloat(summary.toFixed(2)),
        };
      });
      this.tableHeaders = Object.entries({
        id: this.$i18n.t('dialog.billingWaterUsageAnalysis.tableId'),
        alias: this.$i18n.t('dialog.billingWaterUsageAnalysis.tableAlias'),
        ...tableHeadersObject,
        summary: this.$i18n.t('dialog.billingWaterUsageAnalysis.tableSummary'),
      }).map(([value, text]) => {
        return {
          text,
          value,
          align: 'center',
        };
      });
    },
    getChartDatasets() {
      return Object.values(this.mappedValues).map(item => {
        return {
          ...item,
          data: item[`data_${this.parameter}`],
        };
      });
    },
    async getData() {
      this.isFetching = true;
      this.isDataReady = false;
      try {
        const r = await this.getLayerFeatures({
          layer_id: this.billingWaterUsageAnalysisData.layer,
          sorting: [
            {
              attribute_name: `${this.billingWaterUsageAnalysisData.datasource}.date`,
              direction: 'asc',
            },
          ],
          features_filter: {
            '!AND': [
              {
                $GREATER_OR_EQUAL: {
                  [`${this.billingWaterUsageAnalysisData.datasource}.date`]: { value: `${this.dates[0]}-01` },
                },
              },
              {
                $LESS_OR_EQUAL: {
                  [`${this.billingWaterUsageAnalysisData.datasource}.date`]: { value: `${this.dates[1]}-01` },
                },
              },
              {
                $IN: {
                  [`${this.billingWaterUsageAnalysisData.datasource}.watermeter_id`]: this.selectedItems,
                },
              },
            ],
          },
        });
        const featuresArray = r.data.data.features.features;
        if (!featuresArray || featuresArray.length === 0) return;
        this.mappedValues = featuresArray.reduce((acc, obj) => {
          const objProps = obj.properties;
          let key = objProps.watermeter_id;
          if (!acc[key]) {
            const color = this.$_getRandomColor();
            acc[key] = {
              label: objProps.billing_id,
              borderColor: color,
              borderWidth: 1,
              fill: false,
              tension: 0,
              data_sum: [],
              data_day_average: [],
            };
          }
          acc[key].data_sum.push({
            x: objProps.date,
            y: Number.parseFloat(objProps.sum.toFixed(2)),
          });
          acc[key].data_day_average.push({
            x: objProps.date,
            y: Number.parseFloat(objProps.day_average.toFixed(2)),
          });
          return acc;
        }, {});
        this.setTableData();
        this.isFetching = false;
        this.isDataReady = true;
        if (this.maxFeaturesExceeded) return;
        this.$nextTick(() => {
          this.drawChart();
        });
      } catch (e) {
        console.error('error:', e);
      }
    },
    getXAxes() {
      return {
        id: 'bottom',
        type: 'time',
        time: {
          unit: 'month',
          displayFormats: {
            month: 'YYYY-MM',
          },
        },
        bounds: 'ticks',
        position: 'bottom',
      };
    },
    drawChart() {
      if (this.isChartReady) {
        this.chart.destroy();
      }
      this.isChartReady = false;
      const canvas = document.getElementById('chart');
      Chart.plugins.register({
        afterRender: function(c) {
          const ctx = c.chart.ctx;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, c.chart.width, c.chart.height);
          ctx.restore();
        },
      });
      this.chart = new Chart(canvas, {
        options: {
          tooltips: {
            callbacks: {
              labelColor: (tooltipItem, chart) => {
                return {
                  backgroundColor: chart.data.datasets[tooltipItem.datasetIndex].borderColor,
                };
              },
              label: tooltipItems => {
                const { value } = tooltipItems;
                return `${value} [m3]`;
              },
              title: (tooltipItems, data) => {
                return tooltipItems.map(element => `${data.datasets[element.datasetIndex].label}`);
              },
            },
          },
          maintainAspectRatio: false,
          scales: {
            yAxes: [
              {
                id: 'left',
                type: 'linear',
                position: 'left',
                scaleLabel: {
                  display: true,
                  labelString: '[m3]',
                },
              },
            ],
            xAxes: [this.getXAxes()],
          },
        },
        type: 'line',
        data: {
          datasets: this.getChartDatasets(),
        },
      });
      this.isChartReady = true;
    },
    saveHandler() {
      if (this.currentTab === 0) {
        this.$_saveFile(this.chart.toBase64Image(), this.$i18n.t('dialog.body.chart'));
      } else {
        const fileContent = `${this.$i18n.t(
          `dialog.billingWaterUsageAnalysis.${this.parameter}`
        )}\n${this.tableHeaders.map(header => header.text).join(';')}\n${this.tableItems
          .map(item => Object.values(item).join(';'))
          .join('\n')}\n${Object.values(this.tableSummary)
          .map(item => item)
          .join(';')}`;
        const file = new Blob([fileContent], { type: 'text/csv;charset=utf-8;' });
        this.$_saveFile(URL.createObjectURL(file), `${this.parameter}.csv`);
      }
    },
  },
  beforeDestroy() {
    if (this.isChartReady) this.chart.destroy();
  },
};
</script>

<style lang="scss" scoped>
.sticky-footer-table {
  position: sticky;
  bottom: 0;
  background-color: #fff;
}
::v-deep {
  .v-data-table__wrapper {
    max-height: 450px;
  }
  .ag-center-cols-container {
    width: auto !important;
  }
  .v-input--selection-controls__input .v-icon {
    font-size: 19px;
  }
  .v-slide-group {
    z-index: 99;
    position: sticky;
    top: 0;
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
      0px 1px 10px 0px rgba(0, 0, 0, 0.12) !important;
  }
}

.activeTab {
  background-color: rgba(207, 207, 207, 0.25);
}

.top-border {
  @include border(top, 1px);
}

.billingWaterUsageTable {
  @include border();
}
</style>
